import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { ProtectedRoute } from './components/common/ProtectedRoute';
import { AppLayout } from './components/layout/AppLayout';
import { LandingPage } from './pages/LandingPage';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { AdminPage } from './pages/AdminPage';
import { PasswordResetPage } from './pages/PasswordResetPage';
import { DashboardPage } from './pages/DashboardPage';
import { ProjectPage } from './pages/ProjectPage';
import { SettingsPage } from './pages/SettingsPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { ROUTES } from './utils/constants';
import { usePlausiblePageview } from './hooks/usePlausible';

// Lazy load static/passive pages
const AboutPage = lazy(() => import('./pages/AboutPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const DetailsPage = lazy(() => import('./pages/DetailsPage'));
const AlphaOverviewPage = lazy(() => import('./pages/AlphaOverviewPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));

function AppRoutes() {
  usePlausiblePageview();
  return (
    <Routes>
      {/* Public routes */}
      <Route path={ROUTES.HOME} element={<LandingPage />} />
      <Route path={ROUTES.LOGIN} element={<LoginPage />} />
      <Route path={ROUTES.REGISTER} element={<RegisterPage />} />
      <Route path={ROUTES.ADMIN} element={<AdminPage />} />
      <Route path={ROUTES.RESET_PASSWORD} element={<PasswordResetPage />} />
      <Route
        path={ROUTES.ABOUT}
        element={
          <Suspense fallback={<div />}>
            <AboutPage />
          </Suspense>
        }
      />
      <Route
        path={ROUTES.PRIVACY}
        element={
          <Suspense fallback={<div />}>
            <PrivacyPage />
          </Suspense>
        }
      />
      <Route
        path={ROUTES.TERMS}
        element={
          <Suspense fallback={<div />}>
            <TermsPage />
          </Suspense>
        }
      />
      <Route
        path={ROUTES.DETAILS}
        element={
          <Suspense fallback={<div />}>
            <DetailsPage />
          </Suspense>
        }
      />

      <Route
        path={ROUTES.PRICING}
        element={
          <Suspense fallback={<div />}>
            <PricingPage />
          </Suspense>
        }
      />

      {/* Protected routes */}
      <Route element={<ProtectedRoute />}>
        {/* Doc pages — own header/footer via MarkdownPage */}
        <Route
          path={ROUTES.ALPHA_OVERVIEW}
          element={
            <Suspense fallback={<div />}>
              <AlphaOverviewPage />
            </Suspense>
          }
        />
        {/* Project workspace — its own full-viewport shell with one
            consolidated header row; must not stack under the global
            navbar from AppLayout. */}
        <Route path={ROUTES.PROJECT} element={<ProjectPage />} />
        <Route element={<AppLayout />}>
          <Route path={ROUTES.DASHBOARD} element={<DashboardPage />} />
          <Route path={ROUTES.SETTINGS} element={<SettingsPage />} />
        </Route>
      </Route>

      {/* 404 */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  )
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
